// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  activateAdmin,
  adminsSelector,
  deactivateAdmin,
  deleteAdmin,
  getAdminsList,
  selectAdminFilters,
} from '../../feature';

// Components
import { ConfirmationModal, useForm } from '@/components';
import { Pagination } from '@/ui';
import AdminsList from '../../components/AdminList/AdminsList';
import CreateAdminContainer from '../CreateAdminContainer/CreateAdminContainer';
import {
  StyledContentBlock,
  StyledCreateButton,
  StyledDrawer,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledPageWrapper,
  StyledPagination,
  StyledTabsWrap,
} from './AdminsListContainerStyles';

// Models
import { AdminStatus } from '@/models/admins.model';

// Helpers
import { useBoolean } from '@/hooks';
import { GetAdminsListDTO } from '../../dtos';

const AdminsListContainer = () => {
  const { t } = useTranslation(['admins']);
  const dispatch = useAppDispatch();
  const { value: createFormOpened, setFalse: closeCreateForm, setTrue: openCreateForm } = useBoolean();
  const { isLoading, adminsList, pagination } = useAppState(adminsSelector);
  const filters = useAppState(selectAdminFilters);
  const [currentAdminId, setCurrentAdminId] = useState<string | null>(null);
  const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null);
  const [form] = useForm();

  const onDrawerClose = () => {
    form.resetFields();
    setCurrentAdminId(null);
    closeCreateForm();
  };

  const handleSubmit = async () => {
    const dto = new GetAdminsListDTO({ ...filters, limit: pagination?.limit });
    await dispatch(getAdminsList(dto)).unwrap();
    onDrawerClose();
  };

  const getAdminsListHandler = () => {
    const dto = new GetAdminsListDTO({ ...filters });
    dispatch(getAdminsList(dto));
  };

  const handleChangePage = (value: { cursor?: string; limit?: number }) => {
    const dto = new GetAdminsListDTO({ ...filters, ...value });

    dispatch(getAdminsList(dto));
  };

  const handleCancelDelete = () => setDeleteAdminId(null);
  const handleConfirmDelete = async () => {
    if (deleteAdminId) {
      try {
        await dispatch(deleteAdmin(deleteAdminId)).unwrap();
        getAdminsListHandler();
        setDeleteAdminId(null);
        setCurrentAdminId(null);
        onDrawerClose();
      } catch {}
    }
  };

  const onAdminEdit = (id: string) => {
    setCurrentAdminId(id);
    openCreateForm();
  };

  const onAdminStatusUpdate = (id: string, status: AdminStatus) => {
    const updateAction = status === AdminStatus.ACTIVE ? activateAdmin : deactivateAdmin;
    dispatch(updateAction(id));
  };

  useEffect(() => {
    getAdminsListHandler();
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('admins_title')}</StyledPageTitle>
        <StyledTabsWrap>
          <StyledCreateButton onClick={openCreateForm} type={'primary'}>
            {t('admins_add_admin')}
          </StyledCreateButton>
        </StyledTabsWrap>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <AdminsList
          isLoading={isLoading}
          onAdminEdit={onAdminEdit}
          adminsList={adminsList ?? []}
          onDeleteAdmin={setDeleteAdminId}
          updateAdminStatus={onAdminStatusUpdate}
        />
        <StyledPagination>
          <Pagination cursor={pagination ?? {}} onChange={handleChangePage} loading={isLoading} />
        </StyledPagination>
      </StyledContentBlock>
      <StyledDrawer
        open={createFormOpened}
        onClose={onDrawerClose}
        size={'large'}
        title={t(currentAdminId ? 'admin_edit_title' : 'admin_create_title')}
      >
        <CreateAdminContainer form={form} onFormSubmit={handleSubmit} id={currentAdminId} />;
      </StyledDrawer>
      <ConfirmationModal
        isOpened={!!deleteAdminId}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('admin_delete_title')}
      />
    </StyledPageWrapper>
  );
};

export default AdminsListContainer;
